<template>
  <div id="map-container">
    <div id="map" style="max-height: 90vh"></div>
    <div id="polygon-label"></div>
    <p id="coordinates" class="small">{{ mouseMessage }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as turf from "@turf/turf";
import { mapCenter, mapClip, mapClipBounds } from "@/utils/config";

// 1. Define component properties
const props = defineProps({
  geojsonData: {
    type: Array,
    required: true
  },
  satellites: { type: Array, default: () => [] },
  lit: { type: Array, default: () => [] },
  tableSelectedBeam: { type: String, default: null },
});

// 2. Reactive State Values
const map = ref(null);
const beamsLayer = ref(null);
const activeLayers = ref([]);
const selectedBeams = ref([]);
const satMarkers = ref([]);
const satMarkerOffets = ref({});

const emit = defineEmits(['beam-selected']);

const mouseMessage = ref(
  'Move your mouse over a location on the map to calculate visible satellite angles.'
);

const beamStyle = {
  color: 'mediumseagreen',
  fillColor: 'lightgreen',
  colorDark: 'white',
  fillDark: null,
  oDefault: 0.2,
  oHighlight: 0.5,
  oDark: 0,
  wDefault: 0.5,
  wHighlight: 1,
};

const satMarkerStyle = {
  hOffsetDefault: 0,
  vOffsetDefault: 10,
  vStackOffset: 70,
};

// 3. Fix the Date Line Crossings (The Core Optimization Hook)
const processAntimeridianGeoJson = (rawData) => {
  if (!rawData || !Array.isArray(rawData)) return [];
  console.debug(`Normalizing beams across the international date line...`);
  
  // 1. Iterate over each top-level zone FeatureCollection
  return rawData.map(collection => {
    // Safety check to ensure it's a valid FeatureCollection structure
    if (!collection || !Array.isArray(collection.features)) return collection;

    // Deep clone the collection to prevent Vue prop mutation warnings
    const clonedCollection = JSON.parse(JSON.stringify(collection));

    // 2. Iterate and process individual beam features inside this collection
    clonedCollection.features = clonedCollection.features.map(feature => {
      try {
        if (!feature || !feature.geometry) return feature;
        // Step A: Force 2D coordinate arrays by dropping any 3D altitude values
        const flatFeature = turf.truncate(feature, { coordinates: 2 });
        const geom = flatFeature.geometry;
        const name = feature.id;

        if (geom.type === "Polygon") {
          const rings = geom.coordinates;
          let crossesDateLine = false;

          // Step B: Identify coordinate leaps spanning more than 180 degrees longitude
          for (const ring of rings) {
            for (let i = 0; i < ring.length - 1; i++) {
              if (Math.abs(ring[i][0] - ring[i+1][0]) > 180) {
                crossesDateLine = true;
                break;
              }
            }
            if (crossesDateLine) break;
          }

          if (crossesDateLine) {
            const eastCoords = [];
            const westCoords = [];

            rings.forEach(ring => {
              const eastRing = [];
              const westRing = [];

              ring.forEach(([lng, lat]) => {
                // Shift projections cleanly to break the wrapping canvas lines
                const lngEast = lng < 0 ? lng + 360 : lng;
                const lngWest = lng > 0 ? lng - 360 : lng;
                
                eastRing.push([lngEast, lat]);
                westRing.push([lngWest, lat]);
              });

              eastCoords.push(eastRing);
              westCoords.push(westRing);
            });
            console.debug(
              `Converting ${flatFeature.id} to Multipolygon`
            );
            // Step C: Reassemble split shapes as a Leaflet-compliant MultiPolygon
            flatFeature.geometry = {
              type: "MultiPolygon",
              coordinates: [eastCoords, westCoords]
            };
          }
        }
        
        return turf.rewind(flatFeature, { reverse: true });
      } catch (err) {
        console.warn(
          `Could not slice antimeridian boundary` +
          ` for ${feature.id || 'Unknown'}:`, err.message);
        return feature;
      }
    });

    return clonedCollection;
  });
};

// 4. Utility Logic Methods
const isBeamLit = (layer) => {
  const beamName = layer.feature?.id;
  return beamName && props.lit.includes(beamName);
};

const updateDarkBeams = () => {
  if (!beamsLayer.value) return;
  beamsLayer.value.eachLayer((layer) => {
    const lit = isBeamLit(layer);
    if (!lit) {
      layer.setStyle({
        color: beamStyle.colorDark,
        fillColor: beamStyle.fillDark,
      });
    }
  });
};

const addGeoJSONLayer = (geojsonData) => {
  if (beamsLayer.value) {
    beamsLayer.value.remove();
  }

  // Pre-process and normalize the spatial layers crossing 180/-180
  const normalizedData = processAntimeridianGeoJson(geojsonData);

  beamsLayer.value = L.geoJSON(normalizedData, {
    style: {
      color: beamStyle.color,
      weight: beamStyle.wDefault,
      fillColor: beamStyle.fillColor,
      fillOpacity: beamStyle.oDefault,
    },
    onEachFeature: (feature, layer) => {
      layer.feature = feature;
    },
  }).addTo(map.value);

  updateDarkBeams();
};

const clearHighlights = () => {
  activeLayers.value.forEach((layer) => {
    const beamName = layer.feature?.id;
    if (beamName && selectedBeams.value.includes(beamName)) return;
    
    const lit = isBeamLit(layer);
    layer.setStyle({
      color: lit ? beamStyle.color : beamStyle.colorDark,
      fillColor: lit ? beamStyle.fillColor : beamStyle.fillDark,
      weight: beamStyle.wDefault,
      fillOpacity: lit ? beamStyle.oDefault : beamStyle.oDark,
    });
  });
  activeLayers.value = [];
};

const updateHighlights = (newLayers) => {
  clearHighlights();
  if (newLayers.length > 0) {
    newLayers.forEach((layer) => {
      const lit = isBeamLit(layer);
      layer.setStyle({
        color: lit ? beamStyle.color : beamStyle.colorDark,
        fillColor: lit ? beamStyle.fillColor : beamStyle.colorDark,
        weight: beamStyle.wHighlight,
        fillOpacity: beamStyle.oHighlight,
      });
    });
    activeLayers.value = newLayers;
  }
};

const isPointInPolygon = (point, feature) => {
  if (feature.geometry.type === "Polygon" || feature.geometry.type === "MultiPolygon") {
    return turf.booleanPointInPolygon(point, feature);
  }
  return false;
};

const updatePolygonLabel = (names, originalEvent) => {
  // console.debug(`Labeling polygons ${names}`);
  const label = document.getElementById("polygon-label");
  if (names.length > 0 && originalEvent) {
    label.innerHTML = names.join("<br>"); // Show all overlapping names
    label.style.display = "block";
    // Find exactly where the mouse container sits relative to the map box frame
    const mapContainer = document.getElementById("map-container");
    const rect = mapContainer.getBoundingClientRect();
    // Calculate precise mouse tracking positioning relative to the wrapper card
    const x = originalEvent.clientX - rect.left;
    const y = originalEvent.clientY - rect.top;
    label.style.left = `${x + 10}px`;
    label.style.top = `${y + 10}px`;
  } else {
    label.style.display = "none"; // Hide when no polygon is hovered
  }
};

const handleMouseMove = (latlng, originalEvent) => {
  if (!beamsLayer.value) return;
  const point = turf.point([latlng.lng, latlng.lat]);
  let hoveredLayers = [];
  let polygonNames = [];

  beamsLayer.value.eachLayer((layer) => {
    if (isPointInPolygon(point, layer.feature)) {
      if (!Object.hasOwn(layer.feature.properties, 'dark')) {
        hoveredLayers.push(layer);
      }
      if (layer.feature.id) {
        polygonNames.push(layer.feature.id);
      }
    }
  });
  updateHighlights(hoveredLayers);
  updatePolygonLabel(polygonNames, originalEvent);
};

const addSatellites = () => {
  props.satellites.forEach((sat) => {
    const marker = L.marker([0, sat.longitude], {
      icon: L.divIcon({
        className: 'satellite-icon',
        html: '&#x1F6F0;',
      }),
      zIndexOffset: 1000,
    }).addTo(map.value);

    let vOffset = satMarkerStyle.vOffsetDefault;
    const markerPoint = map.value.latLngToLayerPoint(marker.getLatLng());

    satMarkers.value.forEach(({ marker: otherMarker, sat: otherSat }) => {
      const otherPoint = map.value.latLngToLayerPoint(otherMarker.getLatLng());
      const distance = markerPoint.distanceTo(otherPoint);
      if (distance < 50) {
        if (!Object.hasOwn(satMarkerOffets.value, sat.name) && 
            !Object.hasOwn(satMarkerOffets.value, otherSat.name)) {
          satMarkerOffets.value[sat.name] = (vOffset + satMarkerStyle.vStackOffset);
        } else if (Object.hasOwn(satMarkerOffets.value, sat.name)) {
          let satOffset = satMarkerOffets.value[sat.name];
          satMarkerOffets.value[otherSat.name] = (
            satOffset + satMarkerStyle.vStackOffset
          );
        } else {
          let otherOffset = satMarkerOffets.value[otherSat.name];
          satMarkerOffets.value[sat.name] = (
            otherOffset + satMarkerStyle.vStackOffset
          );
        }
      }
    });

    if (!Object.hasOwn(satMarkerOffets.value, sat.name)) {
      satMarkerOffets.value[sat.name] = satMarkerStyle.vOffsetDefault;
    }
    satMarkers.value.push({ marker, sat });
  });
  map.value.on('zoomend moveend', adjustSatelliteTooltipsOffset);
};

const calculateAngles = (sat, lat, lng) => {
  const R_EARTH = 6378;  // Radius of Earth in km
  const R_GEO = 35786; // Height of geostationary orbit in km

  const degToRad = (deg) => deg * Math.PI / 180;
  const radToDeg = (rad) => rad * 180 / Math.PI;

  const dLon = degToRad(lng - sat.longitude);
  const eLat = degToRad(lat);
  const r1 = 1 + R_GEO / R_EARTH;
  const v1 = r1 * Math.cos(eLat) * Math.cos(dLon) - 1;
  const v2 = r1 * Math.sqrt(1 - Math.cos(eLat)**2 * Math.cos(dLon)**2);
  const elevation = radToDeg(Math.atan(v1/v2));

  const eAz = Math.atan(Math.tan(dLon) / Math.sin(eLat));
  let azimuth = 180 + radToDeg(eAz);
  if (eLat < 0) azimuth -= 180;
  if (azimuth < 0) azimuth += 360;

  return {
    azimuth: azimuth,
    elevation: elevation
  };
};

const satTag = (sat) => {
  let ew = sat.longitude < 0 ? 'W' : 'E';
  return `${sat.name} (${Math.abs(sat.longitude)}${ew})`;
};

const adjustSatelliteTooltipsOffset = () => {
  satMarkers.value.forEach(({ marker, sat }, index) => {
    marker.unbindTooltip();
    marker.bindTooltip(satTag(sat), {
      className: 'satellite-tooltip',
      permanent: true,
      direction: 'bottom',
      offset: [0, satMarkerOffets.value[sat.name]],
    });
  });
};

const updateSatelliteTooltips = (latlng) => {
  satMarkers.value.forEach(({marker, sat}) => {
    let azimuth, elevation;
    if (latlng) {
      const { lat, lng } = latlng;
      ({ azimuth, elevation } = calculateAngles(sat, lat, lng));
    }
    if (elevation !== undefined && elevation < 5) {
      marker.unbindTooltip();
    } else {
      let azText = azimuth !== undefined ? `${azimuth.toFixed(0)}&deg;` : 'N/A';
      let elText = elevation !== undefined ? `${elevation.toFixed(0)}&deg;` : 'N/A';
      const tooltipContent = `
        ${satTag(sat)}<br>
        Azimuth: ${azText}<br>
        Elevation: ${elText}
      `;
      if (!marker.getTooltip()) {
        let hOffset = satMarkerStyle.hOffsetDefault;
        let vOffset = satMarkerOffets.value[sat.name];
        marker.bindTooltip(tooltipContent, {
          permanent: true,
          direction: 'bottom',
          className: 'satellite-tooltip',
          offset: [hOffset, vOffset],
        });
      } else {
        marker.setTooltipContent(tooltipContent);
      }
    }
  });
};

const removeSatelliteTooltips = () => {
  satMarkers.value.forEach(({ marker }) => {
    if (marker.getTooltip()) {
      marker.unbindTooltip(); // Remove the tooltip from the marker
    }
  });
};

const toggleHighlightBeam = (layer, isSelected) => {
  if (isSelected) {
    updateHighlights([layer]); // Add to active layers and highlight
  } else {
    clearHighlights(); // Remove highlight if clicked again
  }
};

const handleClick = (event) => {
  const point = turf.point([event.latlng.lng, event.latlng.lat]); // Convert click position to GeoJSON point
  let clickedBeam = null;
  let isSelected = false;

  beamsLayer.value.eachLayer((layer) => {
    if (isPointInPolygon(point, layer.feature)) {
      // Check if the clicked beam is already selected
      clickedBeam = layer.feature.id;
      const isAlreadyHighlighted = selectedBeams.value.includes(clickedBeam);
      isSelected = !isAlreadyHighlighted;
      if (isSelected) {
        selectedBeams.value.push(clickedBeam);
      } else {
        const i = selectedBeams.value.indexOf(clickedBeam);
        if (i != -1) {
          selectedBeams.value.splice(i, 1);
        }
      }

      // Toggle highlighting the selected beam
      toggleHighlightBeam(layer, isSelected);

      // Emit beam name to parent
      const selection = { beamName: clickedBeam, selected: isSelected };
      emit('beam-selected', selection);

    }
  });
};

const initializeMap = () => {
  const mapElement = document.getElementById("map");
  if (!mapElement) {
    console.error(`Unable to locate map-container element`);
    return;
  }
  const minZoom = 2;

  console.debug(`Map clip: ${mapClip} (${JSON.stringify(mapClipBounds)})`);
  map.value = L.map(mapElement, {
    center: mapCenter,
    maxBounds: mapClip ? mapClipBounds : undefined,
    // Prevents the map from rubber-banding when dragged against the edges (1.0 = hard wall)
    maxBoundsViscosity: mapClip ? 1.0 : undefined, 
    minZoom: minZoom,
    // maxZoom: 19,
    zoomControl: true,
    worldCopyJump: false,
  }).setView(mapCenter, minZoom);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
    noWrap: mapClip, // Prevents world wrap
    bounds: mapClip ? mapClipBounds : undefined,
  }).addTo(map.value);

  if (props.geojsonData?.length) {
    addGeoJSONLayer(props.geojsonData);
  }

  addSatellites();

  map.value.on("mousemove", (e) => {
    const coordsEl = document.getElementById("coordinates");
    if (coordsEl) {
      coordsEl.innerText = `Device Location (Lat, Long):` + 
        ` ${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`;
    }
    handleMouseMove(e.latlng, e.originalEvent);
    updateSatelliteTooltips(e.latlng);
  });

  map.value.on("mouseout", () => {
    clearHighlights();
    removeSatelliteTooltips();
    const coordsEl = document.getElementById('coordinates');
    if (coordsEl) {
      coordsEl.innerText = mouseMessage.value;
    }
  });
  
  map.value.on('click', handleClick);

};

// 5. Watchers for Dynamic Property Changes
watch(() => props.geojsonData, (newData) => {
  if (map.value && newData) {
    addGeoJSONLayer(newData);
  }
}, { deep: true });

watch(() => props.lit, () => {
  updateDarkBeams();
}, { deep: true });

// 6. Lifecycles Initialization
onMounted(() => {
  nextTick(() => {
    initializeMap();
  });
});
</script>

<style scoped>

#map-container {
  /* Ensure children use this as a reference */
  position: relative;
  width: 100%;
  padding: 15px;
  box-sizing: border-box;
}

#map {
  /* display: block; */
  /* aspect-ratio: 2 / 1; */
  width: 100%;
  max-width: 1100px;
  height: 520px;
  max-height: 45vh;
  /* Centers the map layout box horizontally */
  margin: 0 auto;
  /* Subtle dashboard box frame border */
  border: 1px solid #dcdcdc;
  /* Clean rounded card styling layout */
  border-radius: 6px;
  /* Professional UI depth shadow */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  /* Forces map canvas backing to match water blue, dropping grey borders */
  background-color: #aad3df;
}

#coordinates {
  font-family: monospace;
  max-width: 1100px;
  padding: 8px 0;
  margin: 0 auto;
}

#polygon-label {
  position: absolute;
  background: rgba(42, 53, 43, 0.5); /* Semi-transparent background */
  color: white;
  padding: 1px;
  border-radius: 5px;
  font-size: 10px;
  white-space: nowrap;
  pointer-events: none; /* Prevent interference with mouse events */
  z-index: 9999;
}

.leaflet-container {
  cursor: crosshair !important;
}
::v-deep(.leaflet-interactive) {
  cursor: crosshair !important;
}
::v-deep(.satellite-icon) {
  font-size: 20px;
  text-align: center;
  line-height: 1;
  /* color: #ff0; */
}
::v-deep(.leaflet-tooltip.satellite-tooltip) {
  background: rgb(253, 252, 252, 0) !important;
  color: darkgreen;
  border: none;
  padding: 5px;
  border-radius: 5px;
  font-size: 12px;
  opacity: 0.8 !important;
  box-shadow: none !important;
}
::v-deep(.leaflet-tooltip-bottom.satellite-tooltip::before) {
  border-bottom-color: rgba(253, 252, 252, 0) !important;
}

</style>
